<script setup>
    import { ref, onMounted } from 'vue';
    import axios from 'axios';
    import * as yup from 'yup';
    import utils from '@/utils.js';
    import { useForm } from 'vee-validate';
    import { useToast } from 'vue-toastification';

    const { meta, errors, handleSubmit, submitCount } = useForm({
        validationSchema: yup.object({
            shipperModelId: yup.number().nullable().required(),
            shippingAccountId: yup.string().required(),
            fromAddress: yup
                .object()
                .shape({
                    country: yup.string().required(),
                    stateProvince: yup.string().required(),
                    city: yup.string().required(),
                    postalCode: yup.string().required(),
                    addressLine1: yup.string().required()
                }),
            toAddress: yup
                .object()
                .shape({
                    country: yup.string().required(),
                    stateProvince: yup.string().required(),
                    city: yup.string().required(),
                    postalCode: yup.string().required(),
                    addressLine1: yup.string().required()
                })
        })
    });

    const busyState = ref({ busy: false, text: 'loading, please wait...' });
    const results = ref(null);
    const shipperModels = ref([]);
    const shippingAccounts = ref([]);
    const stateProvinces = ref([]);

    const model = ref({
        toAddress: { country: 'United States', city: 'Tonawanda', postalCode: '14150', stateProvince: 'NY', addressLine1: '175 East Park Drive' },
        fromAddress: { country: 'United States', city: 'Burr Ridge', postalCode: '60527', stateProvince: 'IL', addressLine1: '7000 High Grove Blvd' },
        shipperModelId: null,
        shippingAccountId : null
    });

    function onInvalidSubmit() {
        useToast().error("One or more validation errors have occurred. Please correct the issues and try again");
        document.getElementsByClassName("errors")[0].scrollIntoView();
    }

    const onSubmit = handleSubmit(async () => {        
        toggleBusy('Calculating rates, this may take a few moments....');
        results.value = null;
        try {
            const response = await axios.post('/api/Shipping/Estimate', model.value);
            if (response.status !== 200) return;
            results.value = response.data;
        }
        finally {
            toggleBusy();
        }
    }, onInvalidSubmit);

    async function loadLookups() {
        await Promise.all([
            axios.get('/api/Tracking/StateProvinces/usa').then(x => stateProvinces.value = x.data),
            axios.get('/api/ShipperModel/List').then(x => shipperModels.value = x.data),
            axios.get('/api/Shipping/Accounts').then(x => shippingAccounts.value = x.data)
        ]);
        
        model.value.shipperModelId = shipperModels.value.length > 0 ? shipperModels.value[0].id : null;
        model.value.shippingAccountId = shippingAccounts.value.length > 0 ? shippingAccounts.value[0].id : null;
    }

    function toggleBusy(text) {
        busyState.value.busy = text != null;
        busyState.value.text = text || '';
    }
    
    onMounted(async () => {      
        toggleBusy('Loading, please wait');
        try {
            await loadLookups();
        }
        finally {
            toggleBusy();
        }
    })
</script>

<template>
    <div class="container">
        <div class="title">Shipping Calculator</div>

        <div class="box">
            <div class="notification is-danger is-light errors" v-show="Object.keys(errors).length > 0 && submitCount > 0">
                <div v-for="error in errors">
                    &bull; {{error}}
                </div>
            </div>

            <form @submit.prevent="onSubmit">
                <div class="field is-horizontal">
                    <div class="field">
                        <label class="label">Carrier</label>
                        <div class="control">
                            <div class="select">
                                <Field as="select" name="shippingAccountId" v-model="model.shippingAccountId">
                                    <option :value="null"></option>
                                    <option v-for="option in shippingAccounts" v-bind:key="option.id" v-bind:value="option.id">
                                        {{option.description}}
                                    </option>
                                </Field>
                            </div>
                        </div>
                    </div>

                    <div class="field">
                        <label class="label">Shipper Model</label>
                        <div class="control">
                            <div class="select">
                                <Field as="select" name="shipperModelId" v-model="model.shipperModelId">
                                    <option :value="null"></option>
                                    <option v-for="option in shipperModels" v-bind:key="option.id" v-bind:value="option.id">
                                        {{option.name}}
                                    </option>
                                </Field>
                            </div>
                        </div>
                    </div>
                </div>

                <p class="subtitle m-0 mb-2"><i class="material-icons">home</i> From Address</p>
                <div class="box">
                    <div class="field">
                        <label class="label">Address</label>
                        <div class="control">
                            <Field class="input" type="text" name="fromAddress.addressLine1" v-model="model.fromAddress.addressLine1" />
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <div class="field-body">
                            <div class="field">
                                <label class="label">City</label>
                                <div class="control">
                                    <Field class="input" type="text" name="fromAddress.city" v-model="model.fromAddress.city" />
                                </div>
                            </div>

                            <div class="field">
                                <label class="label">State / Province</label>
                                <div class="select control is-fullwidth">
                                    <Field as="select" name="fromAddress.stateProvince" v-model="model.fromAddress.stateProvince">
                                        <option :value="null"></option>
                                        <option v-for="option in stateProvinces" v-bind:key="option.abbreviation" v-bind:value="option.abbreviation">
                                            {{option.abbreviation}} - {{option.name}}
                                        </option>
                                    </Field>
                                </div>
                            </div>

                            <div class="field">
                                <label class="label">Postal Code</label>
                                <div class="control">
                                    <Field class="input" type="text" name="fromAddress.postalCode" v-model="model.fromAddress.postalCode" />
                                </div>
                            </div>

                            <div class="field">
                                <label class="label">Country</label>
                                <div class="control">
                                    <Field class="input" type="text" name="fromAddress.country" v-model="model.fromAddress.country" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <p class="subtitle m-0 mb-2"><i class="material-icons">home</i> To Address</p>
                <div class="box">
                    <div class="field">
                        <label class="label">Address</label>
                        <div class="control">
                            <Field class="input" type="text" name="toAddress.addressLine1" v-model="model.toAddress.addressLine1" />
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <div class="field-body">
                            <div class="field">
                                <label class="label">City</label>
                                <div class="control">
                                    <Field class="input" type="text" name="toAddress.city" v-model="model.toAddress.city" />
                                </div>
                            </div>

                            <div class="field">
                                <label class="label">State / Province</label>
                                <div class="select control is-fullwidth">
                                    <Field as="select" name="toAddress.stateProvince" v-model="model.toAddress.stateProvince">
                                        <option :value="null"></option>
                                        <option v-for="option in stateProvinces" v-bind:key="option.abbreviation" v-bind:value="option.abbreviation">
                                            {{option.abbreviation}} - {{option.name}}
                                        </option>
                                    </Field>
                                </div>
                            </div>

                            <div class="field">
                                <label class="label">Postal Code</label>
                                <div class="control">
                                    <Field class="input" type="text" name="toAddress.postalCode" v-model="model.toAddress.postalCode" />
                                </div>
                            </div>

                            <div class="field">
                                <label class="label">Country</label>
                                <div class="control">
                                    <Field class="input" type="text" name="toAddress.country" v-model="model.toAddress.country" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="buttons is-justify-content-right">
                    <button type="submit" class="button is-success" v-bind:disabled="busyState.busy == true">Calculate Shipping Cost</button>
                </div>
            </form>
        </div>

        <div v-if="busyState.busy == true">
            <div class="box">                
                <p class="subtitle mb-5">{{busyState.text}}</p>
                <progress class="progress is-large is-info" max="100">60%</progress>
            </div>
        </div>

        <div v-if="results">
            <div class="table-container" id="rates">
                <table class="table is-striped is-hoverable is-bordered is-narrow is-fullwidth">
                    <thead class="has-background-white-ter">
                        <tr class="has-background-info-light">
                            <th>Service Name</th>
                            <th>Charged Weight</th>
                            <th>Estimated Delivery Date</th>
                            <th>Cost Summary</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in results.rates" v-bind:key="item.id">
                            <td>{{item.service_name}}</td>
                            <td>{{item.charge_weight.value}} {{item.charge_weight.unit}}</td>
                            <td>{{utils.formatDate(item.delivery_date)}}</td>
                            <td>
                                <table class="table is-narrow is-borderless is-fullwidth">
                                    <tbody>
                                        <tr v-for="chargeItem in item.detailed_charges">
                                            <td>
                                                {{chargeItem.type}}
                                            </td>
                                            <td align="right">
                                                <span v-if="chargeItem.type == 'discount'">-</span>
                                                {{utils.formatNumber(chargeItem.charge.amount,2)}} {{chargeItem.charge.currency}}
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th>Total Amount</th>
                                            <th align="right">{{utils.formatNumber(item.total_charge.amount,2)}} {{item.total_charge.currency}}</th>
                                        </tr>
                                    </tfoot>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .table.is-borderless td, .table.is-borderless th {
        border: 0;
    }
</style>